import { DeleteOutlined, EditOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Dropdown, Input, Menu, message, Tooltip, Typography } from 'antd';
import confirm from 'antd/lib/modal/confirm';
import { Breadcrumb, Content, Space, SpacedContent, Table, Title } from 'components/lib';
import { ElementRef, useRef, useState } from 'react';
import { Supplemental, useDeleteSupplementalMutation, useListSupplementalsQuery } from 'services/supplemental.service';
import { formatCurrency, roundTo } from 'utils/helpers/format';
import { sortRows } from 'utils/helpers/general';
import CreateOrUpdateSupplementalModal from './CreateOrUpdateSupplementalModal';

const SupplementalListing = () => {
    const [search, setSearch] = useState('');
    const supplementalModalRef = useRef<ElementRef<typeof CreateOrUpdateSupplementalModal>>(null);

    const { data: supplementalsList } = useListSupplementalsQuery();
    const [deleteSupplemental] = useDeleteSupplementalMutation();

    const filteredData = supplementalsList?.filter((supplemental) => {
        if (search === '') return supplemental;

        const regex = new RegExp(search.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'gi');

        return supplemental.name.match(regex);
    });

    const confirmDeleteSupplemental = (supplemental: Supplemental) => {
        confirm({
            title: 'Delete Supplemental',
            icon: <ExclamationCircleOutlined style={{ color: '#e7202e' }} />,
            content: <p>Are you sure you want to delete {supplemental.name}?</p>,
            okText: 'Confirm',
            okType: 'primary',
            cancelText: 'Cancel',
            onOk() {
                deleteSupplemental(supplemental.id).then((res) => {
                    if ('error' in res) {
                        message.error(`There was an error deleting ${supplemental.name}`);
                        return;
                    }

                    message.success(`${supplemental.name} successfully deleted`);
                });
            },
        });
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: sortRows('name'),
            defaultSortOrder: 'ascend',
            width: 350,
        },
        {
            title: 'Base Unit Price',
            dataIndex: 'base_price_cents',
            key: 'base_price_cents',
            sorter: sortRows('base_price_cents'),
            render: (basePrice: number) => formatCurrency(roundTo(basePrice / 100, 2)),
        },
        {
            title: 'Recurring',
            dataIndex: 'recurring',
            key: 'recurring',
            render: (recurring: boolean) => (recurring ? 'Yes' : 'No'),
        },
        {
            title: '',
            key: 'action',
            dataIndex: 'id',
            width: 100,
            render: (id: number, supplemental: Supplemental) => (
                <Dropdown
                    key={id}
                    overlay={
                        <Menu>
                            <Menu.Item
                                onClick={() => {
                                    supplementalModalRef.current?.setSupplementalToUpdate(supplemental);
                                }}
                            >
                                <SpacedContent>
                                    Edit <EditOutlined />
                                </SpacedContent>
                            </Menu.Item>
                            <Menu.Item onClick={() => confirmDeleteSupplemental(supplemental)} disabled={true}>
                                <SpacedContent>
                                    <Tooltip title='This order is directly tied to DogByte functionality. This order cannot be removed.'>
                                        Remove <DeleteOutlined />
                                    </Tooltip>
                                </SpacedContent>
                            </Menu.Item>
                        </Menu>
                    }
                    placement='bottomRight'
                    arrow
                >
                    <Button>
                        <EllipsisOutlined />
                    </Button>
                </Dropdown>
            ),
        },
    ];

    return (
        <>
            <Breadcrumb path={['Supplementals', 'Dashboard']} />
            <Content>
                <Title level={2}>Supplementals</Title>
                <Space direction='vertical' full>
                    <Typography.Text>{supplementalsList?.length} items</Typography.Text>
                    <SpacedContent>
                        <Space>
                            <Input
                                placeholder='Search Supplemental'
                                suffix={<SearchOutlined />}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Space>
                        <Button
                            onClick={() => {
                                supplementalModalRef.current?.openModal();
                            }}
                        >
                            New Supplemental <PlusOutlined />
                        </Button>
                    </SpacedContent>

                    <Table columns={columns} data={filteredData ?? []} />
                </Space>
                <CreateOrUpdateSupplementalModal ref={supplementalModalRef} />
            </Content>
        </>
    );
};

export default SupplementalListing;
